import React, { ReactNode, useCallback, useMemo } from "react";

import { Divider } from "antd";
import { useTrackingOp } from "../../../../contexts/TrackingOpContext";
import InfoFish from "../../../../pages/PlanOperationPage/Popup/EditOp/_components/InfoFish";
import { datetime } from "../../../../util/library/datetime";
import {
    currentDischargingModeName,
    currentLoadingModelName,
    DELIVERY_TYPE,
    deliveryType,
    dischargingModeName,
    KeyOfStep,
    loadingModeName,
    OP_RUNNING_MODE,
    SHORT_DATETIME_FORMAT,
    STEP_STATUS
} from "../../../../util/varibles/constants";
import { durationTxt, getStepNameV2, getSubStepStatus } from "../../../../util/varibles/global";
import {
    IActivityLog,
    IDeliveryTypeUnit,
    IHarvest2,
    ISubStepActivityLog,
    IUnit
} from "../../../../util/varibles/interface";
import Back from "./Back";
import CurrentProcess from "./CurrentProcess";
import NextStep from "./NextStep";
import SubStep from "./SubStep";

const Footer: React.FC<{ isDone?: boolean, status?: STEP_STATUS, isNext?: boolean }> = ({ isDone, status, isNext = true }) => {
    const [step, set] = useTrackingOp(state => state.step);
    const changeStep = useCallback((value: number) => {
        set({ step: value });
    }, [set]);
    const [activity_log = []] = useTrackingOp(state => state.op?.activity_log);
    const [current_process = 0] = useTrackingOp(state => state.op?.operation?.current_process);

    return <div className='flex justify-between mt-4 pr-5'>
        <div className='mt-5'>
            <Back step={step} changeStep={changeStep} />
        </div>
        <div className='flex flex-col items-center justify-end'>
            {(isNext && current_process >= step) && <NextStep
                isDone={isDone}
                status={status}
                name="Next step"
                className='bt-primary min-h-[100px] w-[150px]'
            >
                <div className='flex flex-col items-center justify-center'>
                    <div>{isDone ? 'Complete' : 'Next step'}</div>
                    {current_process === step &&
                        <div>{datetime(activity_log[step + 1].est_start_time).format(SHORT_DATETIME_FORMAT)}</div>}
                </div>
            </NextStep>}
            <CurrentProcess step={step} changeStep={changeStep} />
        </div>
    </div>
}



interface IStepProps {
    data: IActivityLog
    next?: IActivityLog
    content?: boolean
    children?: ReactNode
}

interface INormalStepProps extends IStepProps {
    isFooter?: boolean
    data: { statusName: string, index?: number }
    & (({ status?: STEP_STATUS } & IActivityLog) | ({ key?: string, unit_id?: string } & ISubStepActivityLog))
}

const estTimeKeyByStatus = {
    [STEP_STATUS.STARTED]: 'est_start_time',
    [STEP_STATUS.COMPLETED]: 'est_finish_time',
}

const realTimeKeyByStatus = {
    [STEP_STATUS.STARTED]: 'real_start_time',
    [STEP_STATUS.COMPLETED]: 'real_finish_time',
}

const getOpRunningMode = (op?: IHarvest2) => op?.op_running_mode || OP_RUNNING_MODE.NORMAL

const Normal: React.FC<INormalStepProps> = ({ data, content, next, isFooter = true, children }) => {
    const [op] = useTrackingOp(state => state.op?.operation);
    const op_running_mode = getOpRunningMode(op as IHarvest2);
    const [activity_log = []] = useTrackingOp(state => state.op?.activity_log);
    const { status, statusName, unit_id, key } = data;
    const Component = viewSteps[next?.key || ''];
    const estKey = estTimeKeyByStatus[status || STEP_STATUS.STARTED]
    const realKey = realTimeKeyByStatus[status || STEP_STATUS.STARTED]
    const { [estKey]: estTime, [realKey]: realTime = 0 } = data as any;
    const { operation_type = "" } = op || {};

    return <div className='flex flex-col'>
        <div>
            <div className='pb-3 mb-[20px] border-b border-1-[#DAE1E7]'>
                <div className='text-[24px] mb-2 text-[var(--color1)!important]'>
                    {status ? `${statusName}${unit_id ? ` at ${unit_id}` : ''}` : (getStepNameV2[operation_type] || getStepNameV2.default)?.({ ...op || {}, key })}
                </div>
                <div className='text-[14px] text-comment'>
                    Planned: {datetime(estTime).format(SHORT_DATETIME_FORMAT)}
                </div>
                {!!realTime && <div className='text-[14px]'>
                    Actual: {datetime(realTime).format(SHORT_DATETIME_FORMAT)}
                </div>}
            </div>
            {(!!next && !!Component) && <Component data={next} content />}
            {children}
        </div>
        {isFooter && <Footer isDone={activity_log.length - 2 === data.index}
            isNext={!content && (!Component || op_running_mode !== OP_RUNNING_MODE.DETAIL)} />}
    </div>
}

const Loading: React.FC<IStepProps> = ({ data, content }) => {
    const [op] = useTrackingOp(state => state.op?.operation);
    const { tanks = [], sub_operations = [] } = op as IHarvest2 || {};
    const op_running_mode = getOpRunningMode(op as IHarvest2);

    const units = useMemo(() => (sub_operations || []).reduce((rs: { [id: string]: IUnit }, item) => {
        rs[item.id] = item;
        return rs;
    }, {}), [sub_operations]);

    const { groupByUnit, isDone, startedIds, target } = useMemo(() => {
        let totalStatus = 0, started: ISubStepActivityLog[] = [], finished: ISubStepActivityLog[] = [];
        const value = data?.sub_steps || [];
        const doneScore = value.length * 2;
        const result = value.reduce((rs: {
            [id: string]: {
                subs: { [id: string]: ISubStepActivityLog },
                est_start_time: number,
                est_finish_time: number,
                status: STEP_STATUS
            }
        }, item) => {
            const { sub_id = '', tank_id } = item;
            const status = getSubStepStatus(item);
            const value = { ...item, status }
            if (status === STEP_STATUS.STARTED)
                started.push(value);
            else if (status === STEP_STATUS.COMPLETED)
                finished.push(value)

            totalStatus += status;
            if (rs[sub_id]) {
                rs[sub_id].subs[tank_id] = value;
            } else {

                rs[sub_id] = { ...value, subs: { [tank_id]: value } }
            }

            return rs;
        }, {});

        started = started.sort((a, b) => (b.real_start_time || 0) - (a.real_start_time || 0));
        finished = finished.sort((a, b) => (b.real_start_time || 0) - (a.real_start_time || 0));

        return {
            groupByUnit: result,
            isDone: totalStatus === doneScore,
            startedIds: new Set(started.map(item => item.group_id)),
            target: started[0] || finished[0]
        }
    }, [data?.sub_steps]);

    return <div className='flex flex-col gap-[20px]'>
        {!content && (() => {
            const dataView = op_running_mode === OP_RUNNING_MODE.DETAIL
                ? { ...target, unit_id: units[target?.sub_id || '']?.unit_id }
                : { ...data, status: getSubStepStatus(data) }

            return <Normal
                isFooter={false}
                data={{ ...dataView, statusName: currentLoadingModelName[dataView.status || ''] || '' }}
                content
            />
        })()}
        {Object.keys(groupByUnit).map(key => {
            const item = groupByUnit[key];
            const duration = item.est_finish_time - item.est_start_time;
            const status = item.status || STEP_STATUS.NOT_START;
            const statusName = loadingModeName[status] || '';

            return <div className='flex flex-col' key={key}>
                <div className='flex justify-center w-full mb-3'>
                    <SubStep
                        status={status}
                        ids={Object.keys(item.subs).map(subKey => item.subs[subKey].group_id)}
                        disabled={op_running_mode !== OP_RUNNING_MODE.DETAIL}
                        started={startedIds}
                        name={<>{statusName.toLowerCase()} {units[key]?.unit_id}</>}
                    >
                        {statusName} {units[key]?.unit_id} - {durationTxt(duration)}
                    </SubStep>
                </div>
                <div className='flex gap-4 w-full'>
                    {tanks.map(tank => {
                        return <div className='flex flex-col w-full' key={tank.id}>
                            <div className='flex justify-center h-[26px]'>
                                <InfoFish
                                    data={item.subs[tank.id]}
                                    keySplit={<Divider className='h-[24px]' orientation='vertical' />}
                                />
                            </div>
                            <div className='tank-op' key={tank.id}>
                                <div className='bg-[var(--bgSidebar1)] px-[10px] flex items-center justify-center' />
                                <div className='flex justify-center items-center px-[10px]'>
                                    <div>{tank.name}</div>
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            </div>
        })}
        {(!content && (isDone || op_running_mode !== OP_RUNNING_MODE.DETAIL)) && <Footer />}
    </div>
}

const Discharging: React.FC<IStepProps> = ({ data, content }) => {
    const [op] = useTrackingOp(state => state.op?.operation);
    const { sub_operations = [], tanks = [] } = op as IHarvest2 || {};
    const op_running_mode = getOpRunningMode(op as IHarvest2);

    const { groupByTank, isDone, startedIds, target } = useMemo(() => {
        let totalStatus = 0, started: ISubStepActivityLog[] = [], finished: ISubStepActivityLog[] = [];
        const value = data?.sub_steps || []
        const doneScore = value.length * 2;
        let result = value.reduce((rs: { [id: string]: ISubStepActivityLog[] }, item) => {
            const status = getSubStepStatus(item);
            const value = { ...item, status };
            if (status === STEP_STATUS.STARTED)
                started.push(value);
            else if (status === STEP_STATUS.COMPLETED)
                finished.push(value)

            totalStatus += status;
            rs[item.tank_id || ""] = [...rs[item.tank_id || ""] || [], value];
            return rs;
        }, {})
        return {
            groupByTank: result,
            isDone: totalStatus === doneScore,
            startedIds: new Set(started.map(item => item.group_id)),
            target: started[0] || finished[0]
        }
    }, [data?.sub_steps]);

    const discharging = sub_operations.reduce((rs: {
        [id: string]: { unit_id?: string, name: string, tanks: { [id: string]: IDeliveryTypeUnit['tanks'][0] } }
    }, {
        delivery_types = [],
        id,
        unit_id
    }) => {
        delivery_types.forEach(sub => rs[id + sub.id] = {
            ...sub,
            unit_id,
            tanks: (sub.tanks || []).reduce((convertTanks: { [id: string]: IDeliveryTypeUnit['tanks'][0] }, tank) => {
                convertTanks[tank.id] = tank;
                return convertTanks;
            }, {})
        })
        return rs;
    }, {})

    return <div className='flex flex-col'>
        {!content && (() => {
            const dataView = op_running_mode === OP_RUNNING_MODE.DETAIL
                ? { ...target, unit_id: discharging[target?.sub_id + (target?.delivery_type_id || '')]?.unit_id }
                : { ...data, status: getSubStepStatus(data) }

            return <Normal
                isFooter={false}
                data={{ ...dataView, statusName: currentDischargingModeName[dataView.status || ''] || '' }}
                content
            />
        })()}
        <div className='flex gap-4 w-full mb-4'>
            {tanks.map(item => {
                const subs = groupByTank[item.id || ""] || [];
                return <div
                    className={`flex flex-col`}
                    key={item.id}
                    style={{ minWidth: `calc(${1 / tanks.length * 100}% - ${(tanks.length - 1) * 16 / tanks.length}px)` }}
                >
                    <div className='tank-op mb-[28px]' key={item.id}>
                        <div className='bg-[var(--bgSidebar1)] px-[10px] flex items-center justify-center'>
                            <InfoFish data={item} keySplit={<Divider className='h-[24px]' orientation='vertical' />} />
                        </div>
                        <div className='flex justify-center items-center px-[10px]'>
                            <div>{item.name}</div>
                        </div>
                    </div>
                    {subs.map(item => {
                        const { delivery_type_id = '', status = STEP_STATUS.NOT_START, group_id, sub_id } = item;
                        const { name = '', tanks } = discharging[sub_id + delivery_type_id] || {};
                        const statusName = dischargingModeName[status] || '';
                        const suffix = name.indexOf(deliveryType[DELIVERY_TYPE.DIRECT].name) === -1 ? 'into' : '';
                        const duration = (item.est_finish_time || 0) - (item.est_start_time || 0);
                        return <div className='flex flex-col items-center' key={group_id}>
                            <div className='w-min mb-2'>
                                <SubStep
                                    key={item.group_id}
                                    ids={[group_id]}
                                    disabled={op_running_mode !== OP_RUNNING_MODE.DETAIL}
                                    status={status}
                                    started={startedIds}
                                    name={<>{statusName.toLowerCase()} {suffix} {name}</>}
                                >
                                    <span className='truncate'>{statusName} {suffix} {name}</span>
                                </SubStep>
                            </div>
                            <div className='flex items-center gap-[1px] text-[14px] h-[20px]'>
                                (<InfoFish data={tanks[item.tank_id]} />)
                            </div>
                            <div className='text-comment text-[14px]'>{durationTxt(duration)}</div>
                        </div>
                    })}
                </div>
            })}
        </div>
        {(!content && (isDone || op_running_mode !== OP_RUNNING_MODE.DETAIL)) && <Footer
            isDone={(isDone && op_running_mode === OP_RUNNING_MODE.DETAIL) || (!!data.real_finish_time && op_running_mode !== OP_RUNNING_MODE.DETAIL)}
            status={getSubStepStatus(data)}
        />}
    </div>
}

const viewSteps: { [id: string]: React.FC<IStepProps> } = {
    [KeyOfStep.HARVEST]: Loading,
    [KeyOfStep.DISCHARGE_FACTORY]: Discharging,
}

export default function StepView({ data, next, children }: IStepProps): React.ReactElement {
    const Component = viewSteps[data?.key] || Normal;

    return <Component data={data} next={next}>
        {children}
    </Component>
}
